import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { Edit, Save, Cancel } from '@mui/icons-material';
import apiService from '../services/apiService';

const PricingPage = () => {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPricing();

      if (response.success) {
        setPricing(response.data);
      } else {
        setError(response.message || 'Failed to load pricing');
        loadMockData();
      }
    } catch (err) {
      console.error('Pricing error:', err);
      setError('Failed to load pricing');
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadMockData = () => {
    const mockPricing = [
      {
        id: '1',
        vehicleType: 'Auto',
        minimumFare: 30,
        minimumDistance: 2,
        baseFareKm: 12,
        pickupFarePerKm: 6,
      },
      {
        id: '2',
        vehicleType: 'Bike',
        minimumFare: 25,
        minimumDistance: 2,
        baseFareKm: 10,
        pickupFarePerKm: 5,
      },
      {
        id: '3',
        vehicleType: 'Mini',
        minimumFare: 50,
        minimumDistance: 3,
        baseFareKm: 15,
        pickupFarePerKm: 6,
      },
      {
        id: '4',
        vehicleType: 'Sedan',
        minimumFare: 80,
        minimumDistance: 3,
        baseFareKm: 18,
        pickupFarePerKm: 8,
      },
      {
        id: '5',
        vehicleType: 'SUV',
        minimumFare: 120,
        minimumDistance: 4,
        baseFareKm: 22,
        pickupFarePerKm: 10,
      },
    ];
    setPricing(mockPricing);
  };

  const handleEdit = (vehicle) => {
    setEditingId(vehicle.id);
    setEditValues(vehicle);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSave = async () => {
    try {
      const response = await apiService.updatePricing(editingId, editValues);

      if (response.success) {
        alert('Pricing updated successfully');
        setEditingId(null);
        fetchPricing();
      } else {
        alert(response.message || 'Failed to update pricing');
      }
    } catch (err) {
      alert('Failed to update pricing');
    }
  };

  const handleChange = (field, value) => {
    setEditValues({ ...editValues, [field]: parseFloat(value) });
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Pricing Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure vehicle pricing for different ride types
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error} - Showing demo data
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Vehicle Type</strong></TableCell>
                  <TableCell><strong>Minimum Fare (₹)</strong></TableCell>
                  <TableCell><strong>Min Distance (km)</strong></TableCell>
                  <TableCell><strong>Base Fare Km (₹/km)</strong></TableCell>
                  <TableCell><strong>Pickup Fare (₹/km)</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricing.map((vehicle) => (
                  <TableRow key={vehicle.id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight={600}>
                        {vehicle.vehicleType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {editingId === vehicle.id ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editValues.minimumFare}
                          onChange={(e) => handleChange('minimumFare', e.target.value)}
                          sx={{ width: 80 }}
                        />
                      ) : (
                        `₹${vehicle.minimumFare}`
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === vehicle.id ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editValues.minimumDistance}
                          onChange={(e) => handleChange('minimumDistance', e.target.value)}
                          sx={{ width: 70 }}
                        />
                      ) : (
                        `${vehicle.minimumDistance} km`
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === vehicle.id ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editValues.baseFareKm}
                          onChange={(e) => handleChange('baseFareKm', e.target.value)}
                          sx={{ width: 70 }}
                        />
                      ) : (
                        `₹${vehicle.baseFareKm}/km`
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === vehicle.id ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editValues.pickupFarePerKm}
                          onChange={(e) => handleChange('pickupFarePerKm', e.target.value)}
                          sx={{ width: 70 }}
                        />
                      ) : (
                        `₹${vehicle.pickupFarePerKm}/km`
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === vehicle.id ? (
                        <Box>
                          <IconButton size="small" color="primary" onClick={handleSave}>
                            <Save fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={handleCancel}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton size="small" onClick={() => handleEdit(vehicle)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Fare Calculation Formula:
            </Typography>
            <Typography variant="body2">
              If Ride Distance ≤ Min Distance: Total = Minimum Fare + Pickup Charge{'\n'}
              If Ride Distance {'>'} Min Distance: Total = Minimum Fare + ((Ride Distance - Min Distance) × Base Fare Km) + Pickup Charge{'\n'}
              Pickup Charge = Driver's Distance to Passenger × Pickup Fare (₹/km)
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PricingPage;
